<template>
  <div class="bulk-operations p-6 bg-white rounded-lg shadow-md">
    <h2 class="text-2xl font-bold mb-6 text-gray-800">Bulk Operations</h2>

    <!-- Security Info Banner -->
    <div class="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
      <div class="flex items-start">
        <svg class="w-6 h-6 text-blue-500 mt-0.5 mr-3" fill="none"
             stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <div>
          <h3 class="font-semibold text-blue-800">Security Notice</h3>
          <p class="text-sm text-blue-700 mt-1">
            Only upload trusted ZIP files from verified sources. Maximum file size: 50MB.
          </p>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Export Section -->
      <div class="export-section p-6 border-2 border-gray-200 rounded-lg
                  hover:border-blue-400 transition-colors">
        <div class="flex items-center mb-4">
          <svg class="w-8 h-8 text-green-600 mr-3" fill="none"
               stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
          </svg>
          <h3 class="text-xl font-semibold text-gray-800">Export Payment Methods</h3>
        </div>

        <p class="text-gray-600 mb-4">
          Download all payment methods and their images as a ZIP archive.
        </p>

        <button
          @click="handleExport"
          :disabled="exportState.isExporting"
          class="w-full px-6 py-3 bg-green-600 text-white rounded-lg font-medium
                 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed
                 transition-colors flex items-center justify-center"
        >
          <svg v-if="exportState.isExporting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
               xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                    stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014
                     12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-if="exportState.isExporting">Exporting...</span>
          <span v-else>Export All</span>
        </button>

        <!-- Export Success/Error Messages -->
        <div v-if="exportState.success"
             class="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          Export successful! File downloaded.
        </div>
        <div v-if="exportState.error"
             class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {{ exportState.error }}
        </div>
      </div>

      <!-- Import Section -->
      <div class="import-section p-6 border-2 border-gray-200 rounded-lg
                  hover:border-blue-400 transition-colors">
        <div class="flex items-center mb-4">
          <svg class="w-8 h-8 text-blue-600 mr-3" fill="none"
               stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
          </svg>
          <h3 class="text-xl font-semibold text-gray-800">Import Payment Methods</h3>
        </div>

        <p class="text-gray-600 mb-4">
          Upload a ZIP archive to import payment methods with smart upsert.
        </p>

        <!-- File Upload Area -->
        <div
          @drop.prevent="handleDrop"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          :class="['border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                   isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300']"
        >
          <label for="file-upload" class="cursor-pointer">
            <input
              ref="fileInput"
              type="file"
              @change="handleFileSelect"
              accept=".zip"
              class="hidden"
              id="file-upload"
            />
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none"
                 stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15
                       13l-3-3m0 0l-3 3m3-3v12"></path>
            </svg>
            <p class="mt-2 text-sm text-gray-600">
              <span class="font-semibold text-blue-600 hover:text-blue-700">Click to upload</span>
              or drag and drop
            </p>
            <p class="text-xs text-gray-500 mt-1">ZIP files only (max 50MB)</p>
          </label>

          <!-- Selected File Info -->
          <div v-if="importState.selectedFile" class="mt-4 p-3 bg-gray-100 rounded-lg">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <svg class="w-6 h-6 text-blue-600 mr-2" fill="none"
                     stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0
                           01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <div class="text-left">
                  <p class="text-sm font-medium text-gray-900">
                    {{ importState.selectedFile.name }}
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ formatFileSize(importState.selectedFile.size) }}
                  </p>
                </div>
              </div>
              <button @click="clearFile" class="text-red-600 hover:text-red-800">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Rate Limit Warning -->
        <div v-if="importState.rateLimitWarning"
             class="mt-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700
                    rounded flex items-start">
          <svg class="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732
                     4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
          </svg>
          <span class="text-sm">
            Please wait {{ importState.remainingCooldown }}s before attempting another import.
          </span>
        </div>

        <!-- Upload Progress -->
        <div v-if="importState.isUploading" class="mt-4">
          <div class="flex justify-between text-sm text-gray-600 mb-1">
            <span>Uploading...</span>
            <span>{{ importState.uploadProgress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-blue-600 h-2 rounded-full transition-all duration-300"
              :style="{ width: importState.uploadProgress + '%' }"
            ></div>
          </div>
        </div>

        <button
          @click="handleImport"
          :disabled="!importState.selectedFile || importState.isUploading ||
                     importState.rateLimitWarning"
          class="w-full px-6 py-3 mt-4 bg-blue-600 text-white rounded-lg font-medium
                 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed
                 transition-colors flex items-center justify-center"
        >
          <svg v-if="importState.isUploading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
               xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                    stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014
                     12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span v-if="importState.isUploading">Importing...</span>
          <span v-else>Import File</span>
        </button>

        <!-- Import Success Messages -->
        <div v-if="importState.success"
             class="mt-4 p-4 bg-green-100 border border-green-400 rounded">
          <h4 class="font-semibold text-green-800 mb-2">Import Successful!</h4>
          <div class="text-sm text-green-700 space-y-1">
            <p>Internet Banking: {{ importState.statistics.internet_banking.created }} created,
               {{ importState.statistics.internet_banking.updated }} updated</p>
            <p>Crypto Currencies: {{ importState.statistics.cryptocurrencies.created }} created,
               {{ importState.statistics.cryptocurrencies.updated }} updated</p>
            <p>Swift: {{ importState.statistics.swift.created }} created,
               {{ importState.statistics.swift.updated }} updated</p>
          </div>
          <button
            @click="refreshData"
            class="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
          >
            Refresh Data
          </button>
        </div>

        <!-- Import Error Messages -->
        <div v-if="importState.error"
             class="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          <p class="font-semibold">Import Failed</p>
          <p class="text-sm mt-1">{{ importState.error }}</p>
        </div>
      </div>
    </div>

    <!-- Security Audit Log (if enabled) -->
    <div v-if="auditLog.length > 0" class="mt-8 p-4 bg-gray-50 rounded-lg">
      <h3 class="text-lg font-semibold mb-3 text-gray-800">Recent Activity</h3>
      <div class="space-y-2">
        <div
          v-for="(log, index) in auditLog.slice(0, 5)"
          :key="index"
          class="flex items-center text-sm text-gray-600 p-2 bg-white rounded"
        >
          <svg class="w-4 h-4 mr-2 text-gray-400" fill="none"
               stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span class="flex-1">{{ log.action }}</span>
          <span class="text-xs text-gray-400">{{ log.timestamp }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onUnmounted } from 'vue';
import useAdminStore from '@/store';

const admin = useAdminStore();

// Security constants
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_MIME_TYPE = 'application/zip';
const RATE_LIMIT_COOLDOWN = 10; // seconds between imports
const MAX_RETRIES = 3;

// Component state
const isDragging = ref(false);
const fileInput = ref(null);

const exportState = reactive({
  isExporting: false,
  success: false,
  error: null,
});

const importState = reactive({
  selectedFile: null,
  isUploading: false,
  uploadProgress: 0,
  success: false,
  error: null,
  statistics: {
    internet_banking: { created: 0, updated: 0, errors: [] },
    cryptocurrencies: { created: 0, updated: 0, errors: [] },
    swift: { created: 0, updated: 0, errors: [] },
  },
  rateLimitWarning: false,
  remainingCooldown: 0,
  lastImportTime: 0,
  retryCount: 0,
});

const auditLog = reactive([]);

// Rate limit timer
let cooldownTimer = null;

// Format file size for display
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / (k ** i)) * 100) / 100} ${sizes[i]}`;
}

// Add audit log entry
function addAuditLog(action) {
  const timestamp = new Date().toLocaleString();
  auditLog.unshift({ action, timestamp });

  // Keep only last 50 entries
  if (auditLog.length > 50) {
    auditLog.pop();
  }
}

// Security: Validate file before processing
function validateFile(file) {
  const errors = [];

  // Check file exists
  if (!file) {
    errors.push('No file selected');
    return { valid: false, errors };
  }

  // Check file extension
  if (!file.name.toLowerCase().endsWith('.zip')) {
    errors.push('File must be a ZIP archive (.zip extension)');
  }

  // Check MIME type
  if (file.type && file.type !== ALLOWED_MIME_TYPE && file.type !== 'application/x-zip-compressed') {
    errors.push('Invalid file type. Only ZIP files are allowed.');
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size exceeds maximum allowed (${formatFileSize(MAX_FILE_SIZE)})`);
  }

  // Check for suspicious file names (path traversal attempts)
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    errors.push('Invalid file name detected');
  }

  // Additional security: Check for extremely small files (likely corrupted or empty)
  if (file.size < 100) {
    errors.push('File appears to be empty or corrupted');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Check rate limiting
function checkRateLimit() {
  const now = Date.now();
  const timeSinceLastImport = (now - importState.lastImportTime) / 1000;

  if (timeSinceLastImport < RATE_LIMIT_COOLDOWN) {
    importState.rateLimitWarning = true;
    importState.remainingCooldown = Math.ceil(RATE_LIMIT_COOLDOWN - timeSinceLastImport);

    // Start countdown
    cooldownTimer = setInterval(() => {
      importState.remainingCooldown -= 1;
      if (importState.remainingCooldown <= 0) {
        importState.rateLimitWarning = false;
        clearInterval(cooldownTimer);
      }
    }, 1000);

    return false;
  }

  return true;
}

// Process file selection with validation
function processFileSelection(file) {
  // Reset previous states
  importState.error = null;
  importState.success = false;

  // Validate file
  const validation = validateFile(file);

  if (!validation.valid) {
    importState.error = validation.errors.join('. ');
    addAuditLog(`File validation failed: ${file?.name || 'unknown'}`);
    return;
  }

  importState.selectedFile = file;
  addAuditLog(`File selected: ${file.name} (${formatFileSize(file.size)})`);
}

// File selection handler
function handleFileSelect(event) {
  const file = event.target.files[0];
  processFileSelection(file);
}

// Drag and drop handler
function handleDrop(event) {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  processFileSelection(file);
}

// Clear selected file
function clearFile() {
  importState.selectedFile = null;
  importState.error = null;
  importState.success = false;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

// Handle export
async function handleExport() {
  exportState.isExporting = true;
  exportState.success = false;
  exportState.error = null;

  try {
    addAuditLog('Export initiated');
    const success = await admin.exportPaymentMethods();

    if (success) {
      exportState.success = true;
      addAuditLog('Export completed successfully');

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        exportState.success = false;
      }, 5000);
    } else {
      throw new Error('Export failed');
    }
  } catch (error) {
    exportState.error = error.message || 'Failed to export payment methods. Please try again.';
    addAuditLog(`Export failed: ${error.message}`);
  } finally {
    exportState.isExporting = false;
  }
}

// Handle import with retry logic
async function handleImport() {
  // Check rate limiting
  if (!checkRateLimit()) {
    return;
  }

  // Final validation before upload
  const validation = validateFile(importState.selectedFile);
  if (!validation.valid) {
    importState.error = validation.errors.join('. ');
    return;
  }

  importState.isUploading = true;
  importState.uploadProgress = 0;
  importState.success = false;
  importState.error = null;
  importState.retryCount = 0;

  try {
    addAuditLog(`Import initiated: ${importState.selectedFile.name}`);

    // Create FormData
    const formData = new FormData();
    formData.append('file', importState.selectedFile);

    // Perform import with progress tracking
    const result = await admin.importPaymentMethods(formData, (progress) => {
      importState.uploadProgress = progress;
    });

    if (result.success) {
      importState.success = true;
      importState.statistics = result.statistics;
      importState.lastImportTime = Date.now();
      addAuditLog(`Import completed: ${result.statistics.internet_banking.created + result.statistics.cryptocurrencies.created + result.statistics.swift.created} records created`);

      // Clear file after successful import
      setTimeout(() => {
        clearFile();
      }, 5000);
    } else {
      throw new Error(result.error || 'Import failed');
    }
  } catch (error) {
    // Retry logic for network errors
    if (importState.retryCount < MAX_RETRIES && error.message.includes('network')) {
      importState.retryCount += 1;
      addAuditLog(`Retrying import (attempt ${importState.retryCount}/${MAX_RETRIES})`);
      setTimeout(() => handleImport(), 2000);
      return;
    }

    importState.error = error.message || 'Failed to import payment methods. Please check the file and try again.';
    addAuditLog(`Import failed: ${error.message}`);
  } finally {
    if (importState.retryCount >= MAX_RETRIES) {
      importState.isUploading = false;
    }
  }
}

// Refresh data after import
async function refreshData() {
  await admin.getBanks();
  addAuditLog('Data refreshed');
}

// Cleanup on component unmount
onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer);
  }
});
</script>

<style scoped>
.bulk-operations {
  max-width: 1200px;
  margin: 0 auto;
}

/* Add smooth transitions */
* {
  transition: all 0.2s ease-in-out;
}

/* Prevent text selection during drag */
[draggable] {
  user-select: none;
}
</style>
